// The logistics stoplight for one glazing row. Mirrors Div10LogiCtrl, keyed on the glazing row.
import {useState, useContext, useMemo, useEffect} from "react";
import {
    CircularProgress,
    DialogContent, DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Tab,
    Tabs,
    Typography,
    Button,
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import Close from "@mui/icons-material/Close";
import {useAsync2} from "nate-react-api-helpers";
import {useBackgroundAction} from "../../manage/sov/UseBackgroundTask";
import {api} from "../../../../api/API";
import {Dot, LogiCtrlStoplightButton} from "../openings/logictrl/OpeningCell";
import {useProjectId} from "../ProjectName";
import {LogiCtrlRule, LogiCtrlStatus} from "../../../../api/LogiCtrl";
import {Popover} from "../../../../misc/Popover";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {Row} from "../openings/logictrl/Row";
import {OpeningsExistingTab} from "../openings/logictrl/OpeningsExistingTab";
import {UserRuleInstructions} from "../openings/logictrl/UserRuleInstructions";

export function GlazingLogiCtrlCell(props: {
    glazingItem: number;
    status: {
        edited: boolean;
        error: string | null;
        skipRules: boolean;
    };
}) {
    const [show, setShow] = useState<HTMLElement | null>(null);

    return (
        <>
            <LogiCtrlStoplightButton onClick={e => {
                e.stopPropagation();
                setShow(e.currentTarget);
            }}>
                <Dot edited={props.status?.edited} error={props.status?.error} />
            </LogiCtrlStoplightButton>

            {show && <GlazingLogiCtrlModal
                anchorEl={show}
                glazingItem={props.glazingItem}
                onClose={() => setShow(null)}
                edited={props.status.edited}
                error={props.status.error}
                skipRules={props.status.skipRules}
            />}
        </>
    );
}

function GlazingLogiCtrlModal(props: {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    glazingItem: number;
    edited: boolean;
    error: string | null;
    skipRules: boolean;
}) {
    const projectId = useProjectId();

    const rulesReq = useAsync2((input) => {
        return api.logiCtrl.listRules(input)
    }, {glazing: props.glazingItem, project: projectId}, [props.glazingItem, projectId]);

    const statusReq = useAsync2((input) => {
        return api.logiCtrl.listStatuses(input)
    }, {glazing: props.glazingItem, project: projectId}, [props.glazingItem, projectId]);

    const ready = !statusReq.loadingOrError && !rulesReq.loadingOrError;

    const [tab, setTab] = useState<"rules" | "existing">("rules");
    const [requireRecalcOnClose, setRequireRecalcOnClose] = useState(false);

    const recalcAndClose = useBackgroundAction<Parameters<typeof api.logiCtrl.applyExisting>[0], any>({
        callback: async (input) => api.logiCtrl.applyExisting(input),
        onDone: () => {
            props.onClose();
        }
    });

    const onClose = () => {
        if (requireRecalcOnClose) {
            recalcAndClose.callback({
                project: projectId,
                glazing: props.glazingItem
            })
            return;
        }
        props.onClose();
    }

    const existingCount = useMemo(() => {
        return statusReq.asList.filter(v => !v.autoReplace).reduce((sum, s) => sum + (s.removeInvRequests?.length || 0), 0);
    }, [statusReq.asList]);

    if (recalcAndClose.LoadingElement) {
        return (
            <Dialog2 open>
                <DialogTitle>Applying Background Changes...</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This may take a moment.
                    </DialogContentText>
                    {recalcAndClose.LoadingElement}
                </DialogContent>
            </Dialog2>
        )
    }

    return (
        <Popover open anchorEl={props.anchorEl} onClose={onClose}
                 slotProps={{paper: {sx: {borderRadius: 2, minWidth: 240}}}}>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 8px 4px 12px",
            }}>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                }}>
                    <Typography variant="subtitle2" color="text.secondary">Logistics Status</Typography>
                    {requireRecalcOnClose && <span style={{
                        width: 6, height: 6, borderRadius: "50%",
                        backgroundColor: "#1976d2", display: "inline-block",
                    }} />}
                    {!ready && <CircularProgress size="1em" />}
                </div>
                <IconButton size="small" onClick={onClose} sx={{padding: "2px"}}>
                    <Close sx={{fontSize: 16}} />
                </IconButton>
            </div>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{minHeight: 0, px: 1}}>
                <Tab label="Rules" value="rules" sx={{minHeight: 0, py: 0.5, textTransform: "none", fontSize: 12}} />
                <Tab label={`Existing (${existingCount})`} value="existing" sx={{minHeight: 0, py: 0.5, textTransform: "none", fontSize: 12}} />
            </Tabs>
            <Divider />
            {statusReq.error ? statusReq.LoadingOrErrorElement : null}
            {rulesReq.error ? rulesReq.LoadingOrErrorElement : null}
            {tab === "rules" && (
                <GlazingRulesTab
                    glazingItem={props.glazingItem}
                    edited={props.edited}
                    error={props.error}
                    skipRules={props.skipRules}
                    onClose={onClose}
                    ready={ready}
                    rules={rulesReq.asList}
                    statuses={statusReq.asList}
                />
            )}
            {tab === "existing" && (
                <GlazingExistingTab
                    glazingItem={props.glazingItem}
                    project={projectId}
                    onNeedsRecalc={setRequireRecalcOnClose}
                    onReload={() => {
                        rulesReq.reload();
                        statusReq.reload();
                    }}
                />
            )}
        </Popover>
    )
}

function GlazingRulesTab(props: {
    glazingItem: number;
    edited: boolean;
    error: string | null;
    skipRules: boolean;
    onClose: () => void;
    ready: boolean;
    rules: LogiCtrlRule[];
    statuses: LogiCtrlStatus[];
}) {
    const [rules, setRules] = useState<LogiCtrlRule[]>([]);

    const serverRules = props.rules;
    useEffect(() => {
        setRules(serverRules);
    }, [serverRules]);

    const projectId = useProjectId();
    const [edited, setEdited] = useState(false);

    const onRuleChange = (old: LogiCtrlRule | null, updated: LogiCtrlRule) => {
        setEdited(true);
        setRules(list => {
            if (old) {
                return list.map(r => r === old ? updated : r);
            }
            return [...list, updated];
        })
    }

    const editCtx = useContext(EditContext);

    const save = useBackgroundAction({
        callback: async (input: Parameters<typeof api.logiCtrl.upsertRules>[0]) => {
            return api.logiCtrl.upsertRules(input);
        },
        onDone: () => {
            try {
                editCtx.reload();
            } catch (e) {}
            props.onClose();
        }
    });

    return (
        <div style={{padding: "6px 12px 12px 12px", display: "flex", flexDirection: "column", gap: 6, maxWidth: 450}}>
            <Row
                title="Glazing"
                error={props.error}
                edited={props.edited}
                skipRules={props.skipRules}
                filter={{
                    kind: "glazing",
                    glazingItem: props.glazingItem,
                    project: projectId,
                }}
                rules={rules}
                statuses={props.statuses}
                ready={props.ready}
                onRuleChange={onRuleChange}
            />
            {edited && (
                <div style={{display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 4}}>
                    {save.LoadingElement}
                    <Button size="small" onClick={() => props.onClose()} disabled={save.loading} sx={{textTransform: "none", fontSize: 12}}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={() => save.callback({
                        rules,
                        project: projectId,
                        opening: null,
                        div10: null,
                        miscProduct: null,
                        glazing: props.glazingItem,
                    })} disabled={save.loading} sx={{textTransform: "none", fontSize: 12}}>
                        Save
                    </Button>
                </div>
            )}
            <UserRuleInstructions />
        </div>
    )
}

function GlazingExistingTab(props: {
    glazingItem: number;
    project: number;
    onNeedsRecalc(tf: boolean): void;
    onReload?(): void;
}) {
    return <OpeningsExistingTab
        opening={0}
        project={props.project}
        glazing={props.glazingItem}
        onNeedsRecalc={props.onNeedsRecalc}
        onReload={props.onReload}
    />
}
